// Settings → Users → Archived: everything taken out of service across the other sub-tabs — deactivated people,
// archived technicians and revoked field access — with one-click restore. Nothing is ever deleted from here.
import { useCallback, useEffect, useState } from "react";
import { Archive, RotateCcw, UserCheck, Users as UsersIcon, Wrench, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SettingsPanel, PanelHeader, EmptyState, LoadingState, TABLE_HEAD_CLASS } from "@/components/settings/SettingsUI";
import { listUsers, updateUser, getTechnicians, restoreTechnician, listFieldUsers, reinstateFieldUser } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

const errMsg = (e) => e?.response?.data?.detail || e?.message || "Failed";
const fmt = (s) => (s ? String(s).replace("T", " ").slice(0, 16) : "—");
const roleLabel = (r) => (r === "office" ? "Accounting" : r === "viewer" ? "Viewer (read-only)" : String(r || "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));

function Section({ icon: Icon, title, count, children, testId }) {
  return (
    <div className="border-t border-slate-200 first:border-t-0" data-testid={testId}>
      <div className="flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <Icon className="h-3.5 w-3.5" /> {title} <span className="num text-slate-400">({count})</span>
      </div>
      {children}
    </div>
  );
}

export default function ArchivedPanel({ onChanged }) {
  const { can, isNamedAdmin } = useAuth();
  const canWrite = can("settings.users", "write");
  const [people, setPeople] = useState([]);
  const [techs, setTechs] = useState([]);
  const [field, setField] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [u, t, f] = await Promise.all([listUsers(), getTechnicians({ includeArchived: true }), listFieldUsers().catch(() => ({ data: [] }))]);
      setPeople((u.data || []).filter((x) => x.is_disabled));
      setTechs((t.data || []).filter((x) => x.is_archived));
      setField((f.data || []).filter((x) => x.field_status === "revoked" && !x.is_disabled));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); reload(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const total = people.length + techs.length + field.length;
  return (
    <SettingsPanel testId="archived-panel">
      <PanelHeader icon={Archive} title="Archived" count={total || null} hint="People who no longer work at ABS, archived technicians and revoked field access. Restore any time — nothing here is deleted." />
      {loading ? <LoadingState testId="archived-loading" /> : total === 0 ? (
        <EmptyState icon={Archive} title="Nothing archived" hint="Deactivated people, archived technicians and revoked field users show up here." testId="archived-empty" />
      ) : (
        <div>
          {people.length > 0 && (
            <Section icon={UsersIcon} title="People" count={people.length} testId="archived-people">
              <table className="w-full text-sm">
                <thead className={TABLE_HEAD_CLASS}><tr>
                  <th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Role</th><th className="px-3 py-2 text-right">Actions</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {people.map((u) => (
                    <tr key={u.id} className="opacity-70" data-testid={`archived-user-${u.id}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{u.name || "—"}<Badge variant="outline" className="ml-2 border-0 bg-rose-50 text-[10px] text-rose-600">Deactivated</Badge></td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">{u.email}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{roleLabel(u.role)}</td>
                      <td className="px-3 py-2 text-right">
                        {canWrite && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-emerald-600 hover:bg-emerald-50" disabled={u.passcode_protected && !isNamedAdmin}
                            title={u.passcode_protected && !isNamedAdmin ? "Only an admin can restore this account" : "Restore — brings them back to People with their role intact"}
                            onClick={() => act(() => updateUser(u.id, { is_disabled: false }), `${u.email} restored`)} data-testid={`archived-reactivate-${u.id}`}>
                            <UserCheck className="mr-1 h-3.5 w-3.5" /> Restore
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
          {techs.length > 0 && (
            <Section icon={Wrench} title="Technicians" count={techs.length} testId="archived-technicians">
              <table className="w-full text-sm">
                <thead className={TABLE_HEAD_CLASS}><tr>
                  <th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Phone</th><th className="px-3 py-2 text-right">Actions</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {techs.map((t) => (
                    <tr key={t.id} className="opacity-70" data-testid={`archived-tech-${t.id}`}>
                      <td className="px-3 py-2 font-medium text-slate-800"><span className="mr-2 inline-block h-3 w-3 rounded-full border border-dashed border-slate-300 align-middle" style={{ backgroundColor: t.color || "#3b82f6" }} />{t.name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">{t.email || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{t.phone || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        {canWrite && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-emerald-600 hover:bg-emerald-50" onClick={() => act(() => restoreTechnician(t.id), `${t.name} restored`)} data-testid={`archived-restore-tech-${t.id}`}>
                            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
          {field.length > 0 && (
            <Section icon={Smartphone} title="Field Access" count={field.length} testId="archived-field">
              <table className="w-full text-sm">
                <thead className={TABLE_HEAD_CLASS}><tr>
                  <th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Revoked</th><th className="px-3 py-2 text-right">Actions</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {field.map((u) => (
                    <tr key={u.id} className="opacity-70" data-testid={`archived-field-${u.id}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{u.name}<Badge variant="outline" className="ml-2 border-0 bg-rose-50 text-[10px] uppercase text-rose-600">revoked</Badge></td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">{u.email}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{fmt(u.field_revoked_at)}</td>
                      <td className="px-3 py-2 text-right">
                        {canWrite && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-emerald-600 hover:bg-emerald-50" onClick={() => act(() => reinstateFieldUser(u.id), "Reinstated — sign-in link emailed")} data-testid={`archived-reinstate-${u.id}`}>
                            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reinstate
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
        </div>
      )}
    </SettingsPanel>
  );
}
